import React, { useState } from 'react';
import { VStack, HStack, Text, Link, Icon, Heading, Button, Flex, Box } from '@chakra-ui/react';
import { Globe, CaretDown, CaretUp, BookOpen, FileText, Article } from '@phosphor-icons/react';

const SourceIcon = ({ type }) => {
  switch (type) {
    case 'book':
      return <Icon as={BookOpen} boxSize="1.2em" mt={1} color="text.accent" />;
    case 'document':
      return <Icon as={FileText} boxSize="1.2em" mt={1} color="text.accent" />;
    case 'wiki':
    case 'web':
      return <Icon as={Globe} boxSize="1.2em" mt={1} color="text.accent" />;
    default:
      return <Icon as={Article} boxSize="1.2em" mt={1} color="text.accent" />;
  }
};

const getSafeHtmlId = (referenceId) => {
  if (!referenceId) return '';
  // Remove brackets and ensure it starts with a letter, replace other invalid chars
  return `ref-${referenceId.replace(/[\[\]\s#.]/g, '')}`;
};

const GroundingSources = ({ references = [] }) => {
  const [expanded, setExpanded] = useState(true); // Default to expanded
  
  if (!references.length) return null;

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <VStack align="stretch" spacing={3} mt={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Heading as="h4" size="md" fontFamily="heading"> 
          References
        </Heading>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={toggleExpanded}
          leftIcon={<Icon as={expanded ? CaretUp : CaretDown} />}
        >
          {expanded ? "Hide" : "Show All"} ({references.length})
        </Button>
      </Flex>
      {expanded && references.map((ref, index) => {
        const safeId = getSafeHtmlId(ref.id);
        return (
          <HStack key={safeId || index} id={safeId} align="start" spacing={3} mt={2} py={2} borderBottomWidth="1px" borderColor="border.subtle">
            <SourceIcon type={ref.type} />
            <VStack align="start" spacing={0.5} fontSize="sm" flex="1">
              <Link 
                href={ref.url} 
                isExternal={ref.url && ref.url.startsWith('http')} // Ensure isExternal is true for http/https links
                color="interactive.link" 
                fontWeight="medium" 
                display="flex" 
                alignItems="center"
              >
                <Text as="span" mr={1}>{ref.id}</Text> {ref.title || 'Untitled Source'}
              </Link>
              {ref.referenceText && (
                <Text color="text.muted" fontSize="xs" lineHeight="short" fontStyle="italic">
                  {ref.referenceText}
                </Text>
              )}
              {ref.description && ref.description !== ref.referenceText && (
                 <Text color="text.default" fontSize="xs" lineHeight="short" mt={1}>
                  {ref.description}
                </Text>
              )}
               {Array.isArray(ref.retrievedInfo) && ref.retrievedInfo.length > 0 && (
                <Box mt={1} pl={2} borderLeftWidth="2px" borderColor="brand.accentAlpha">
                  {ref.retrievedInfo.map((info, infoIdx) => (
                    <Text key={infoIdx} fontSize="xs" color="text.subtle" fontStyle="italic">
                      "{info.replace(ref.id, '').trim()}"
                    </Text>
                  ))}
                </Box>
              )}
            </VStack>
          </HStack>
        );
      })}
    </VStack>
  );
};
export default GroundingSources;
